import { format } from 'date-fns';
import {
  Clock,
  Download,
  Edit,
  Eye,
  FileText,
  Filter,
  Mail,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
  UserPlus,
  X
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useGrandeEcole from '../../stores/useGrandeEcole';


export function ElevesManager({ darkMode = true, isEditable = false }) {
  const {
    learners = [],
    cohortes = [],
    staff = [],
    loading,
    updateLearner,
    createLearner,
    deleteLearner,
    createCohorte,
    // Nouvelles actions bulk
    bulkUpdateLearners,
    exportLearnersCSV,
    importLearnersCSV,
    fetchLearnerHistory,
  } = useGrandeEcole();

  const openCompose = useGrandeEcole(state => state.openCompose);

  // ═══════════════════════════════════════════════════════════════════════
  // ÉTATS
  // ═══════════════════════════════════════════════════════════════════════

  // Recherche & Filtres
  const [search, setSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    cohorte: 'all',
    progress: { min: 0, max: 100 },
    pesas: { min: 0, max: Infinity },
    tags: [],
    dateRange: 'all'
  });

  // Édition
  const [editedLearners, setEditedLearners] = useState({});
  const [newLearnerForm, setNewLearnerForm] = useState(false);
  const [newLearnerData, setNewLearnerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthDate: '',
    cohorteId: '',
    studentId: '',
    specialNeeds: '',
    tags: [],
    notes: '',
    stats: { totalPesas: 0 }
  });

  // Sélection multiple
  const [selectedLearners, setSelectedLearners] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  // Modals
  const [detailModal, setDetailModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);

  // Création cohorte rapide
  const [showCreateCohorte, setShowCreateCohorte] = useState(false);
  const [newCohorteQuick, setNewCohorteQuick] = useState({
    nom: '',
    debut: new Date().toISOString().split('T')[0],
    finPrevue: '',
    responsableId: '',
    status: 'active'
  });

  // Tri
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const availableTags = [
    'Débutant', 'Intermédiaire', 'Avancé', 'VIP', 'À suivre',
    'Risque décrochage', 'Performant', 'Besoin accompagnement'
  ];

  // Filtrage avancé
  const filteredLearners = useMemo(() => {
    let result = [...learners];

    // Recherche texte
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(l =>
        `${l.firstName || l.prenom || ''} ${l.lastName || l.nom || ''}`.toLowerCase().includes(searchLower) ||
        l.email?.toLowerCase().includes(searchLower) ||
        l.studentId?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre status
    if (filters.status !== 'all') {
      result = result.filter(l => l.status === filters.status);
    }

    // Filtre cohorte
    if (filters.cohorte !== 'all') {
      result = result.filter(l => (l.cohorte?._id || l.cohorteId) === filters.cohorte);
    }

    // Filtre progression
    result = result.filter(l => {
      const progress = l.globalProgress || 0;
      return progress >= filters.progress.min && progress <= filters.progress.max;
    });

    // Filtre pesas
    result = result.filter(l => {
      const pesas = l.stats?.totalPesas || l.totalPesas || 0;
      return pesas >= filters.pesas.min && pesas <= filters.pesas.max;
    });

    // Filtre tags
    if (filters.tags.length > 0) {
      result = result.filter(l =>
        l.tags && filters.tags.some(t => l.tags.includes(t))
      );
    }

    // Tri
    result.sort((a, b) => {
      let valA, valB;
      
      switch (sortBy) {
        case 'name':
          valA = `${a.lastName || a.nom} ${a.firstName || a.prenom}`;
          valB = `${b.lastName || b.nom} ${b.firstName || b.prenom}`;
          break;
        case 'progress':
          valA = a.globalProgress || 0;
          valB = b.globalProgress || 0;
          break;
        case 'pesas':
          valA = a.stats?.totalPesas || a.totalPesas || 0;
          valB = b.stats?.totalPesas || b.totalPesas || 0;
          break;
        case 'status':
          valA = a.status || 'unknown';
          valB = b.status || 'unknown';
          break;
        default:
          return 0;
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [learners, search, filters, sortBy, sortDir]);

  // ═══════════════════════════════════════════════════════════════════════
  // ACTIONS - ÉDITION
  // ═══════════════════════════════════════════════════════════════════════

  const startEditLearner = (learner) => {
    const id = learner._id || learner.userId || learner.id  ;
    setEditedLearners(prev => ({
      ...prev,
      [id]: {
        ...learner,
        firstName: learner.firstName || learner.prenom || '',
        lastName: learner.lastName || learner.nom || '',
        email: learner.email || '',
        phoneNumber: learner.phoneNumber || '',
        birthDate: learner.birthDate || '',
        studentId: learner.studentId || '',
        cohorteId: learner.cohorte?._id || learner.cohorteId || '',
        specialNeeds: learner.specialNeeds || '',
        status: learner.status || 'active',
        stats: {
          totalPesas: learner.stats?.totalPesas || learner.totalPesas || 0,
          completedActivities: learner.stats?.completedActivities || 0,
          averageScore: learner.stats?.averageScore || 0,
          studyTimeMinutes: learner.stats?.studyTimeMinutes || 0
        },
        tags: learner.tags || [],
        notes: learner.notes || '',
        linkedUserId: learner.linkedUser?._id || learner.linkedUserId
      }
    }));
  };

  const updateEditedField = (id, field, value) => {
    setEditedLearners(prev => {
      const current = prev[id];
      
      // Validation temps réel
      let validated = value;
      
      if (field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          validated = { value, invalid: true };
        }
      }
      
      if (field === 'phoneNumber') {
        validated = value.replace(/[^\d+\s()-]/g, '');
      }
      
      if (field.startsWith('stats.')) {
        const statField = field.split('.')[1];
        return {
          ...prev,
          [id]: {
            ...current,
            stats: {
              ...current.stats,
              [statField]: Number(value) || 0
            }
          }
        };
      }
      
      return {
        ...prev,
        [id]: { ...current, [field]: validated }
      };
    });
  };

  const saveLearner = async (id) => {
    const edited = editedLearners[id];
    if (!edited) return;

    // Validation finale
    const errors = [];
    if (!edited.firstName) errors.push('Prénom obligatoire');
    if (!edited.lastName) errors.push('Nom obligatoire');
    if (!edited.email) errors.push('Email obligatoire');
    if (edited.email?.invalid) errors.push('Email invalide');
    if (!edited.cohorteId) errors.push('Cohorte obligatoire');

    if (errors.length > 0) {
      toast.error(`Erreurs de validation :\n- ${errors.join('\n- ')}`);
      return;
    }

    const confirm = window.confirm(
      `Sauvegarder les modifications ?\n\n` +
      `Nom : ${edited.lastName} ${edited.firstName}\n` +
      `Email : ${edited.email}\n` +
      `Cohorte : ${cohortes.find(c => c._id === edited.cohorteId)?.nom || 'N/A'}\n` +
      `Status : ${edited.status}`
    );

    if (!confirm) return;

    try {
      const response = await updateLearner(id, {
        firstName: edited.firstName,
        lastName: edited.lastName,
        email: edited.email,
        phoneNumber: edited.phoneNumber,
        birthDate: edited.birthDate,
        studentId: edited.studentId,
        cohorteId: edited.cohorteId,
        specialNeeds: edited.specialNeeds,
        status: edited.status,
        stats: edited.stats,
        tags: edited.tags,
        notes: edited.notes
      });

      if (!response.success) {
        // L'API a échoué (ex: 404, 403, etc.)
        toast.error(`❌ Erreur : ${response.error}`);
        return;
      }

      setEditedLearners(prev => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });

      toast.success('Inscription mise à jour avec succès');
    } catch (err) {
      console.error('Erreur sauvegarde inscription:', err);
      toast.error(`❌ Erreur : ${err.message}`);
    }
  };

  const cancelEditLearner = (id) => {
    setEditedLearners(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const confirmDeleteLearner = async (id) => {
    if (!window.confirm('Supprimer cet inscription ? Cette action est irréversible.')) return;
    try {
      await deleteLearner(id);
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleCreateQuickCohorte = async () => {
    if (!newCohorteQuick.nom || !newCohorteQuick.responsableId) {
      alert('Nom et responsable obligatoires pour créer une cohorte');
      return;
    }

    try {
      const result = await createCohorte({
        nom: newCohorteQuick.nom,
        debut: newCohorteQuick.debut,
        finPrevue: newCohorteQuick.finPrevue,
        responsableId: newCohorteQuick.responsableId,
        status: newCohorteQuick.status,
        parcoursIds: []  // Vide par défaut, à compléter après
      });

      if (result.success) {
        // Assigner automatiquement la nouvelle cohorte au learner
        setNewLearnerData(prev => ({
          ...prev,
          cohorteId: result.cohorte._id || result.cohorte.id
        }));
        
        setShowCreateCohorte(false);
        setNewCohorteQuick({
          nom: '',
          debut: new Date().toISOString().split('T')[0],
          finPrevue: '',
          responsableId: '',
          status: 'active'
        });

        alert(`Cohorte "${result.cohorte.nom}" créée avec succès !`);
      }
    } catch (err) {
      console.error('Erreur création cohorte rapide:', err);
      alert('Erreur lors de la création de la cohorte');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // ACTIONS BULK
  // ═══════════════════════════════════════════════════════════════════════

  const toggleSelectLearner = (id) => {
    setSelectedLearners(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllLearners = () => {
    const allIds = filteredLearners.map(l =>  l._id || l.userId || l.id);
    setSelectedLearners(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedLearners(new Set());
  };

  const handleBulkAction = async () => {
    if (selectedLearners.size === 0) {
      alert('Aucune inscription sélectionné');
      return;
    }

    if (!bulkAction) {
      alert('Veuillez sélectionner une action');
      return;
    }

    const learnerIds = Array.from(selectedLearners);

    switch (bulkAction) {
      case 'change-status':
        const newStatus = prompt('Nouveau status (active, inactive, suspended) :');
        if (!newStatus) return;
        
        if (!['active', 'inactive', 'suspended'].includes(newStatus)) {
          alert('Status invalide');
          return;
        }

        try {
          await bulkUpdateLearners(learnerIds, { status: newStatus });
          showToast(`${learnerIds.length} inscriptions mises à jour`, 'success');
          clearSelection();
        } catch (err) {
          showToast(`❌ Erreur bulk update : ${err.message}`, 'error');
        }
        break;

      case 'change-cohorte':
        const cohorteId = prompt('ID de la nouvelle cohorte :');
        if (!cohorteId) return;

        try {
          await bulkUpdateLearners(learnerIds, { cohorteId });
          showToast(`${learnerIds.length} inscriptions transférées`, 'success');
          clearSelection();
        } catch (err) {
          showToast(`❌ Erreur transfert cohorte : ${err.message}`, 'error');
        }
        break;

      case 'add-tags':
        const tags = prompt('Tags à ajouter (séparés par des virgules) :');
        if (!tags) return;

        const tagList = tags.split(',').map(t => t.trim());
        try {
          await bulkUpdateLearners(learnerIds, { 
            $push: { tags: { $each: tagList } } 
          });
          showToast(`Tags ajoutés à ${learnerIds.length} inscriptions`, 'success');
          clearSelection();
        } catch (err) {
          showToast(`❌ Erreur ajout tags : ${err.message}`, 'error');
        }
        break;

      case 'export-selected':
        try {
          await exportLearnersCSV(learnerIds);
          showToast(`Export de ${learnerIds.length} inscriptions réussi`, 'success');
        } catch (err) {
          showToast(`❌ Erreur export : ${err.message}`, 'error');
        }
        break;

      case 'delete-selected':
        const confirmDelete = window.confirm(
          `⚠️ ATTENTION : Supprimer ${learnerIds.length} inscription(s) ?\n\n` +
          `Cette action est IRRÉVERSIBLE.`
        );
        if (!confirmDelete) return;

        const confirmCode = prompt('Tapez "SUPPRIMER" pour confirmer :');
        if (confirmCode !== 'SUPPRIMER') {
          alert('Suppression annulée');
          return;
        }

        try {
          for (const id of learnerIds) {
            await deleteLearner(id);
          }
          showToast(`${learnerIds.length} inscriptions supprimées`, 'success');
          clearSelection();
        } catch (err) {
          showToast(`❌ Erreur suppression : ${err.message}`, 'error');
        }
        break;

      default:
        alert('Action non implémentée');
    }

    setBulkAction('');
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // IMPORT/EXPORT
  // ═══════════════════════════════════════════════════════════════════════

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const confirmImport = window.confirm(
      `Importer des inscriptions depuis "${file.name}" ?\n\n` +
      `⚠️ Les emails existants seront ignorés.`
    );

    if (!confirmImport) return;

    try {
      const result = await importLearnersCSV(file);
      
      showToast(
        `Import réussi\n` +
        `Créés : ${result.created}\n` +
        `Ignorés : ${result.skipped}\n` +
        `Erreurs : ${result.errors}`,
        'success'
      );
    } catch (err) {
      showToast(`❌ Erreur import : ${err.message}`, 'error');
    }

    // Reset input
    event.target.value = '';
  };

  const handleExportAll = async () => {
    try {
      await exportLearnersCSV();
      showToast('Export complet réussi', 'success');
    } catch (err) {
      showToast(`❌ Erreur export : ${err.message}`, 'error');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MODAL DÉTAILS ENRICHI
  // ═══════════════════════════════════════════════════════════════════════

  const openDetailModal = (learner) => {
    setDetailModal(learner);
  };

  const closeDetailModal = () => {
    setDetailModal(null);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const showToast = (message, type = 'info') => {
    // TODO : Intégrer react-toastify ou sonner
    console.log(`[TOAST ${type}]`, message);
    alert(message);
  };

  const activeCount = learners.filter(e => e.status === 'active').length;
  const availableResponsables = staff.filter(s => 
    ['admin', 'formateur', 'instructeur', 'facilitateur', 'Directeur Pédagogique', 'Facilitateur Senior', 'collectivité', 'entreprise', 'pro']
      .includes(s.role || s.mission)
  );

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-zinc-800 animate-pulse rounded" />
        <div className="h-96 bg-zinc-800 animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* HEADER ENRICHI */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-zinc-800 pb-4 gap-4">
          <div>
            <h3 className="text-2xl font-black tracking-tighter text-orange-400 uppercase">
              Gestion
            </h3>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
              {filteredLearners.length} / {learners.length} affichés – {activeCount} en mission active
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Recherche */}
            <div className="relative flex-1 md:flex-none">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher nom, email, ID..."
                className="w-full md:w-64 pl-10 pr-4 py-2 text-sm font-mono border rounded-none outline-none focus:border-orange-500/50 transition-all bg-zinc-900/50 border-zinc-800 text-zinc-200 placeholder-zinc-600"
              />
            </div>

            {/* Filtres avancés */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`px-4 py-2 text-xs font-bold uppercase border transition-all ${
                showAdvancedFilters
                  ? 'bg-orange-600/30 border-orange-500/50 text-orange-300'
                  : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Filter size={14} className="inline mr-2" />
              Filtres
            </button>

            {/* Import/Export */}
            <div className="relative group">
              <button className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs font-bold uppercase transition-all">
                <FileText size={14} className="inline mr-2" />
                Import/Export
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-zinc-900 border border-zinc-700 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <button
                  onClick={handleExportAll}
                  className="w-full px-4 py-2 text-left text-xs hover:bg-zinc-800 text-zinc-300 flex items-center gap-2"
                >
                  <Download size={12} />
                  Exporter tout (CSV)
                </button>
                <label className="w-full px-4 py-2 text-left text-xs hover:bg-zinc-800 text-zinc-300 flex items-center gap-2 cursor-pointer">
                  <Upload size={12} />
                  Importer CSV
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleImportCSV}
                  />
                </label>
              </div>
            </div>

            {/* Bouton Recruter */}
            <button
              onClick={() => setNewLearnerForm(!newLearnerForm)}
              className="px-5 py-2 bg-orange-600/20 hover:bg-orange-600/40 border border-orange-500/30 text-orange-300 font-bold text-xs uppercase tracking-widest transition-all flex items-center gap-2"
            >
              <Plus size={14} />
              Ajouter
            </button>
          </div>
        </div>
        
        {newLearnerForm && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-6">
    <div className="w-full max-w-lg max-h-[90vh] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden flex flex-col">
      
      {/* Header fixe */}
      <div className="shrink-0 px-6 py-4 border-b border-zinc-800 bg-zinc-950/80 flex items-center justify-between">
        <h3 className="text-lg font-bold text-orange-400 tracking-tight">Nouvel entrant</h3>
        <button
          onClick={() => setNewLearnerForm(false)}
          className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
      </div>

      {/* Corps avec scroll */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Identité - grid compact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase text-zinc-500 mb-1.5">Prénom *</label>
            <input
              value={newLearnerData.firstName}
              onChange={e => setNewLearnerData({ ...newLearnerData, firstName: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-zinc-500 mb-1.5">Nom *</label>
            <input
              value={newLearnerData.lastName}
              onChange={e => setNewLearnerData({ ...newLearnerData, lastName: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase text-zinc-500 mb-1.5">Email *</label>
          <input
            type="email"
            value={newLearnerData.email}
            onChange={e => setNewLearnerData({ ...newLearnerData, email: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase text-zinc-500 mb-1.5">Téléphone</label>
            <input
              type="tel"
              value={newLearnerData.phoneNumber}
              onChange={e => setNewLearnerData({ ...newLearnerData, phoneNumber: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-zinc-500 mb-1.5">Date de naissance</label>
            <input
              type="date"
              value={newLearnerData.birthDate}
              onChange={e => setNewLearnerData({ ...newLearnerData, birthDate: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
            />
          </div>
        </div>

        {/* Cohorte + bouton création rapide */}
        <div>
          <label className="block text-xs uppercase text-zinc-500 mb-1.5">Cohorte *</label>
          <div className="flex gap-2.5 items-end">
            <select
              value={newLearnerData.cohorteId}
              onChange={e => setNewLearnerData({ ...newLearnerData, cohorteId: e.target.value })}
              className="flex-1 bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
              required
            >
              <option value="">— Choisir une cohorte —</option>
              {cohortes.map(c => (
                <option key={c._id} value={c._id}>
                  {c.nom} {c.eleves ? `(${c.eleves})` : ''}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setShowCreateCohorte(!showCreateCohorte)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                showCreateCohorte
                  ? 'bg-emerald-700/70 text-white border-emerald-500/40'
                  : 'bg-emerald-900/40 hover:bg-emerald-800/50 border border-emerald-700/50 text-emerald-200'
              }`}
              title="Créer une nouvelle cohorte"
            >
              <Plus size={16} />
              Nouvelle
            </button>
          </div>

          {/* Mini-formulaire création cohorte (affiché inline) */}
          {showCreateCohorte && (
            <div className="mt-4 p-4 bg-emerald-950/30 border border-emerald-800/40 rounded-lg space-y-4 animate-in fade-in slide-in-from-top-1">
              <h6 className="text-sm font-semibold text-emerald-300">Créer cohorte rapide</h6>

              <div className="space-y-3.5">
                <input
                  type="text"
                  placeholder="Nom de la cohorte *"
                  value={newCohorteQuick.nom}
                  onChange={e => setNewCohorteQuick(p => ({ ...p, nom: e.target.value }))}
                  className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-emerald-500/70 outline-none"
                />

                <select
                  value={newCohorteQuick.responsableId}
                  onChange={e => setNewCohorteQuick(p => ({ ...p, responsableId: e.target.value }))}
                  className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-emerald-500/70 outline-none"
                >
                  <option value="">Responsable...</option>
                  {availableResponsables.map(r => (
                    <option key={r._id} value={r._id}>
                      {r.firstName} {r.lastName?.slice(0,1)} • {r.role?.slice(0,12)}
                    </option>
                  ))}
                </select>

                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={newCohorteQuick.debut}
                    onChange={e => setNewCohorteQuick(p => ({ ...p, debut: e.target.value }))}
                    className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm rounded-lg focus:border-emerald-500/70"
                  />
                  <input
                    type="date"
                    value={newCohorteQuick.finPrevue}
                    onChange={e => setNewCohorteQuick(p => ({ ...p, finPrevue: e.target.value }))}
                    className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm rounded-lg focus:border-emerald-500/70"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowCreateCohorte(false)}
                  className="px-4 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleCreateQuickCohorte}
                  disabled={!newCohorteQuick.nom.trim() || !newCohorteQuick.responsableId}
                  className="px-5 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800/50 disabled:text-zinc-400 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Save size={14} />
                  Créer & assigner
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Champs optionnels plus bas */}
        <div>
          <label className="block text-xs uppercase text-zinc-500 mb-1.5">Tags (séparés par virgule)</label>
          <input
            value={newLearnerData.tags?.join(', ') || ''}
            onChange={e => setNewLearnerData({
              ...newLearnerData,
              tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
            })}
            placeholder="VIP, À suivre, Risque décrochage..."
            className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none transition-all"
          />
        </div>

        <div>
          <label className="block text-xs uppercase text-zinc-500 mb-1.5">Notes / commentaire d'accueil</label>
          <textarea
            value={newLearnerData.notes || ''}
            onChange={e => setNewLearnerData({ ...newLearnerData, notes: e.target.value })}
            rows={3}
            className="w-full bg-zinc-950 border border-zinc-700 px-3.5 py-2.5 text-sm rounded-lg focus:border-orange-500/70 outline-none resize-none transition-all"
            placeholder="Contexte d'inscription, motivation..."
          />
        </div>
      </div>

      {/* Footer fixe en bas du modal */}
      <div className="shrink-0 px-6 py-4 border-t border-zinc-800 bg-zinc-950/80 flex justify-end gap-3">
        <button
          onClick={() => setNewLearnerForm(false)}
          className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={async () => {            
            if (!newLearnerData.firstName?.trim() || !newLearnerData.lastName?.trim() || 
                !newLearnerData.email?.trim() || !newLearnerData.cohorteId) {
              toast.error("Champs obligatoires manquants");
              return;
            }
            try {
              await createLearner(newLearnerData);
              toast.success("Inscription créé avec succès");
              setNewLearnerForm(false);
              // reset form...
            } catch (err) {
              toast.error(err.message || "Erreur lors de la création");
            }
          }}
          className="px-6 py-2.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
        >
          <UserPlus size={16} />
          Créer 
        </button>
      </div>
    </div>
  </div>
)}

        {/* FILTRES AVANCÉS PANNEAU */}
        {showAdvancedFilters && (
          <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded space-y-4 animate-in fade-in slide-in-from-top-2">
            <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Filtres Avancés</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filtre Status */}
              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={e => setFilters({ ...filters, status: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-orange-500"
                >
                  <option value="all">Tous</option>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                  <option value="suspended">Suspendu</option>
                </select>
              </div>

              {/* Filtre Cohorte */}
              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Cohorte</label>
                <select
                  value={filters.cohorte}
                  onChange={e => setFilters({ ...filters, cohorte: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-orange-500"
                >
                  <option value="all">Toutes</option>
                  {cohortes.map(c => (
                    <option key={c._id || c.id} value={c._id || c.id}>
                      {c.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtre Progression */}
              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">
                  Progression ({filters.progress.min}% - {filters.progress.max}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={filters.progress.max}
                  onChange={e => setFilters({
                    ...filters,
                    progress: { ...filters.progress, max: Number(e.target.value) }
                  })}
                  className="w-full"
                />
              </div>

              {/* Filtre Pesas */}
              <div>
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Pesas min</label>
                <input
                  type="number"
                  min="0"
                  value={filters.pesas.min}
                  onChange={e => setFilters({
                    ...filters,
                    pesas: { ...filters.pesas, min: Number(e.target.value) }
                  })}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-orange-500"
                />
              </div>

              {/* Filtre Tags */}
              <div className="md:col-span-4">
                <label className="block text-[10px] uppercase text-zinc-500 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => {
                        const already = filters.tags.includes(tag);
                        setFilters({
                          ...filters,
                          tags: already
                            ? filters.tags.filter(t => t !== tag)
                            : [...filters.tags, tag],
                        });
                      }}
                      className={`px-3 py-1 text-[10px] border transition-all ${
                        filters.tags.includes(tag)
                          ? 'bg-orange-950/40 border-orange-500/50 text-orange-300'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setFilters({
                  status: 'all',
                  cohorte: 'all',
                  progress: { min: 0, max: 100 },
                  pesas: { min: 0, max: Infinity }
                })}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setShowAdvancedFilters(false)}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}

        {/* BARRE ACTIONS BULK */}
        {selectedLearners.size > 0 && (
          <div className="bg-sky-950/20 border border-sky-800/50 p-4 rounded flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sky-300 font-bold">
                {selectedLearners.size} sélectionné(s)
              </span>
              <select
                value={bulkAction}
                onChange={e => setBulkAction(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500"
              >
                <option value="">Choisir une action...</option>
                <option value="change-status">Changer le status</option>
                <option value="change-cohorte">Transférer de cohorte</option>
                <option value="add-tags">Ajouter des tags</option>
                <option value="export-selected">Exporter sélection</option>
                <option value="delete-selected">❌ Supprimer sélection</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-xs font-medium"
              >
                Exécuter
              </button>
              <button
                onClick={selectAllLearners}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs"
              >
                Tout sélectionner
              </button>
              <button
                onClick={clearSelection}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs"
              >
                Désélectionner
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TABLEAU ENRICHI AVEC SÉLECTION MULTIPLE */}
      <div className="border border-zinc-800 overflow-hidden bg-black/40">

        {/* Header tableau — desktop uniquement */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] font-black uppercase tracking-widest border-b bg-zinc-900/60 border-zinc-700 text-zinc-400">
          <div className="col-span-1 flex items-center justify-center">
            <input
              type="checkbox"
              checked={selectedLearners.size === filteredLearners.length && filteredLearners.length > 0}
              onChange={(e) => e.target.checked ? selectAllLearners() : clearSelection()}
              className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-orange-500"
            />
          </div>
          {[
            { key: 'name',     label: 'IDENTITÉ',    col: 'col-span-3' },
            { key: 'cohorte',  label: 'COHORTE',     col: 'col-span-2' },
            { key: 'status',   label: 'STATUS',      col: 'col-span-1 text-center' },
            { key: 'progress', label: 'PROGRESSION', col: 'col-span-2 text-center' },
            { key: 'pesas',    label: 'PESAS',       col: 'col-span-1 text-right' },
          ].map(({ key, label, col }) => (
            <div
              key={key}
              className={`${col} cursor-pointer hover:text-orange-400 transition-colors flex items-center gap-1`}
              onClick={() => {
                if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                else { setSortBy(key); setSortDir('asc'); }
              }}
            >
              {label}
              {sortBy === key && (
                <span className="text-orange-400">{sortDir === 'asc' ? '↑' : '↓'}</span>
              )}
            </div>
          ))}
        </div>

        {/* Rows */}
        <div className="divide-y divide-zinc-800/40 max-h-[600px] overflow-y-auto">
          {filteredLearners.length > 0 ? (
            filteredLearners.map(el => {
              const id = el._id || el.userId || el.id;
              const isEdited = !!editedLearners[id];
              const isSelected = selectedLearners.has(id);
              const current = isEdited ? editedLearners[id] : el;
              const cohorte = cohortes.find(c => c._id === (current.cohorte?._id || current.cohorteId));

              return (
                <div
                  key={id}
                  className={`transition-colors ${
                    isSelected ? 'bg-sky-950/20 border-l-2 border-sky-500' : 'hover:bg-orange-950/10'
                  }`}
                >

                  {/* ═══════════════════════════════════════ */}
                  {/* VUE MOBILE : carte compacte            */}
                  {/* ═══════════════════════════════════════ */}
                  <div className="md:hidden px-4 py-3 space-y-2.5">

                    {/* Ligne 1 : checkbox + avatar + nom + status */}
                    <div className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectLearner(id)}
                        className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-orange-500 shrink-0"
                      />
                      <div className="w-8 h-8 shrink-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center text-orange-400 font-black border border-orange-500/30 text-xs rounded">
                        {((current.lastName || current.nom) || '??').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        {isEdited ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={current.lastName || ''}
                              onChange={e => updateEditedField(id, 'lastName', e.target.value)}
                              placeholder="Nom"
                              className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-orange-500 rounded"
                            />
                            <input
                              type="text"
                              value={current.firstName || ''}
                              onChange={e => updateEditedField(id, 'firstName', e.target.value)}
                              placeholder="Prénom"
                              className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-orange-500 rounded"
                            />
                            <input
                              type="email"
                              value={current.email || ''}
                              onChange={e => updateEditedField(id, 'email', e.target.value)}
                              placeholder="Email"
                              className={`w-full bg-zinc-900 border px-2 py-1 text-xs outline-none focus:border-orange-500 rounded ${
                                current.email?.invalid ? 'border-red-500' : 'border-zinc-700'
                              }`}
                            />
                            {current.email?.invalid && (
                              <span className="text-[9px] text-red-400">Email invalide</span>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="font-bold text-sm truncate">
                              {current.lastName || current.nom} {current.firstName || current.prenom || ''}
                            </div>
                            <div className="text-[10px] text-zinc-500 font-mono truncate">
                              {current.email || '—'}
                            </div>
                          </>
                        )}
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 text-[9px] font-black border tracking-tighter rounded ${
                        current.status === 'active'    ? 'bg-emerald-900/20 text-emerald-400 border-emerald-500/30'
                        : current.status === 'suspended' ? 'bg-red-900/20 text-red-400 border-red-500/30'
                        : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                      }`}>
                        {(current.status || '—').toUpperCase().slice(0, 4)}
                      </span>
                    </div>

                    {/* Ligne 2 : cohorte + progression + pesas */}
                    <div className="flex items-center gap-3 pl-[52px]">
                      <span className="text-[10px] text-zinc-500 shrink-0 truncate max-w-[80px]">
                        {isEdited ? (
                          <select
                            value={current.cohorteId || ''}
                            onChange={e => updateEditedField(id, 'cohorteId', e.target.value)}
                            className="bg-zinc-900 border border-zinc-700 px-1.5 py-1 text-xs outline-none focus:border-orange-500 rounded max-w-[90px]"
                          >
                            <option value="">Cohorte...</option>
                            {cohortes.map(c => (
                              <option key={c._id || c.id} value={c._id || c.id}>{c.nom}</option>
                            ))}
                          </select>
                        ) : (
                          cohorte?.nom || '—'
                        )}
                      </span>
                      <div className="flex-1 bg-zinc-800 h-1 rounded overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all"
                          style={{ width: `${current.globalProgress || 0}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500 shrink-0 w-8 text-right">
                        {current.globalProgress || 0}%
                      </span>
                      <span className="text-[10px] font-mono text-orange-400 font-bold shrink-0">
                        {isEdited ? (
                          <input
                            type="number"
                            value={current.stats?.totalPesas || 0}
                            onChange={e => updateEditedField(id, 'stats.totalPesas', Number(e.target.value))}
                            min="0"
                            className="w-16 bg-zinc-900 border border-zinc-700 px-1.5 py-0.5 text-xs text-right outline-none focus:border-orange-500 rounded"
                          />
                        ) : (
                          <>{(current.stats?.totalPesas || current.totalPesas || 0).toLocaleString()}<span className="opacity-60">₧</span></>
                        )}
                      </span>
                    </div>

                    {/* Ligne 3 : actions */}
                    <div className="flex items-center justify-end gap-1 pl-[52px]">
                      {isEdited ? (
                        <>
                          <button onClick={() => saveLearner(id)}
                            className="p-2 hover:text-emerald-400 transition-colors" title="Sauvegarder">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditLearner(id)}
                            className="p-2 hover:text-red-400 transition-colors" title="Annuler">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => openDetailModal(el)}
                            className="p-2 hover:text-sky-400 transition-colors" title="Détails">
                            <Eye size={14} />
                          </button>
                          <button onClick={() => startEditLearner(el)} disabled={!isEditable}
                            className={`p-2 transition-colors ${isEditable ? 'hover:text-orange-400' : 'opacity-40 cursor-not-allowed'}`}
                            title={isEditable ? "Éditer" : "Activez Mode Lab"}>
                            <Edit size={14} />
                          </button>
                          <button onClick={() => openCompose(el)}
                            className="p-2 hover:text-sky-400 transition-colors" title="Contacter">
                            <Mail size={14} />
                          </button>
                          <button onClick={() => confirmDeleteLearner(id)} disabled={!isEditable}
                            className={`p-2 transition-colors ${isEditable ? 'hover:text-red-500' : 'opacity-40 cursor-not-allowed'}`}
                            title={isEditable ? "Supprimer" : "Activez Mode Lab"}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ═══════════════════════════════════════ */}
                  {/* VUE DESKTOP md+ : tableau 12 col        */}
                  {/* ═══════════════════════════════════════ */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">

                    {/* CHECKBOX */}
                    <div className="col-span-1 flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectLearner(id)}
                        className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-orange-500"
                      />
                    </div>

                    {/* IDENTITÉ */}
                    <div className="col-span-3 flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center text-orange-400 font-black border border-orange-500/30 text-xs rounded">
                        {((current.lastName || current.nom) || '??').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 truncate">
                        {isEdited ? (
                          <div className="space-y-1">
                            <input type="text" value={current.lastName || ''} onChange={e => updateEditedField(id, 'lastName', e.target.value)}
                              placeholder="Nom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-orange-500 rounded" />
                            <input type="text" value={current.firstName || ''} onChange={e => updateEditedField(id, 'firstName', e.target.value)}
                              placeholder="Prénom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-orange-500 rounded" />
                            <input type="email" value={current.email || ''} onChange={e => updateEditedField(id, 'email', e.target.value)}
                              placeholder="Email" className={`w-full bg-zinc-900 border px-2 py-1 text-xs outline-none focus:border-orange-500 rounded ${current.email?.invalid ? 'border-red-500' : 'border-zinc-700'}`} />
                            {current.email?.invalid && <span className="text-[9px] text-red-400">Email invalide</span>}
                          </div>
                        ) : (
                          <>
                            <div className="font-bold text-sm truncate">{current.lastName || current.nom} {current.firstName || current.prenom || ''}</div>
                            <div className="text-[10px] text-zinc-500 font-mono truncate">{current.email || '—'}</div>
                            {current.studentId && <div className="text-[9px] text-zinc-600 font-mono">ID: {current.studentId}</div>}
                          </>
                        )}
                      </div>
                    </div>

                    {/* COHORTE */}
                    <div className="col-span-2">
                      {isEdited ? (
                        <select value={current.cohorteId || ''} onChange={e => updateEditedField(id, 'cohorteId', e.target.value)}
                          className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-orange-500 rounded">
                          <option value="">Sélectionner...</option>
                          {cohortes.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.nom}</option>)}
                        </select>
                      ) : (
                        <div className="text-sm text-zinc-300">{cohorte?.nom || '—'}</div>
                      )}
                    </div>

                    {/* STATUS */}
                    <div className="col-span-1 flex justify-center">
                      {isEdited ? (
                        <select value={current.status || 'active'} onChange={e => updateEditedField(id, 'status', e.target.value)}
                          className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs outline-none focus:border-orange-500 rounded">
                          <option value="active">Actif</option>
                          <option value="inactive">Inactif</option>
                          <option value="suspended">Suspendu</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-0.5 text-[9px] font-black border tracking-tighter rounded ${
                          current.status === 'active'     ? 'bg-emerald-900/20 text-emerald-400 border-emerald-500/30'
                          : current.status === 'suspended' ? 'bg-red-900/20 text-red-400 border-red-500/30'
                          : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                        }`}>
                          {(current.status || 'inconnu').toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* PROGRESSION */}
                    <div className="col-span-2 flex items-center gap-3 justify-center">
                      <div className="flex-1 bg-zinc-800 h-1.5 rounded overflow-hidden max-w-32">
                        <div className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all"
                          style={{ width: `${current.globalProgress || 0}%` }} />
                      </div>
                      <span className="text-[11px] font-mono text-zinc-500 w-10">{current.globalProgress || 0}%</span>
                    </div>

                    {/* PESAS */}
                    <div className="col-span-1 font-mono text-orange-400 text-right font-bold">
                      {isEdited ? (
                        <input type="number" value={current.stats?.totalPesas || 0}
                          onChange={e => updateEditedField(id, 'stats.totalPesas', Number(e.target.value))}
                          min="0" className="w-24 bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-right outline-none focus:border-orange-500 rounded" />
                      ) : (
                        <>{(current.stats?.totalPesas || current.totalPesas || 0).toLocaleString()}<span className="text-[10px] opacity-60">₧</span></>
                      )}
                    </div>

                    {/* ACTIONS */}
                    <div className="col-span-2 flex items-center justify-end gap-2 opacity-60 hover:opacity-100 transition-opacity">
                      {isEdited ? (
                        <>
                          <button onClick={() => saveLearner(id)} className="p-1.5 hover:text-emerald-400 transition-colors" title="Sauvegarder"><Save size={14} /></button>
                          <button onClick={() => cancelEditLearner(id)} className="p-1.5 hover:text-red-400 transition-colors" title="Annuler"><X size={14} /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => openDetailModal(el)} className="p-1.5 hover:text-sky-400 transition-colors" title="Voir détails"><Eye size={14} /></button>
                          <button onClick={() => startEditLearner(el)} disabled={!isEditable}
                            className={`p-1.5 transition-colors ${isEditable ? 'hover:text-orange-400 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                            title={isEditable ? "Éditer" : "Activez Mode Lab"}><Edit size={14} /></button>
                          <button onClick={() => setHistoryModal(el)} className="p-1.5 hover:text-purple-400 transition-colors" title="Historique"><Clock size={14} /></button>
                          <button onClick={() => openCompose(el)} className="p-1.5 hover:text-sky-400 transition-colors" title="Contacter"><Mail size={14} /></button>
                          <button onClick={() => confirmDeleteLearner(id)} disabled={!isEditable}
                            className={`p-1.5 transition-colors ${isEditable ? 'hover:text-red-500 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                            title={isEditable ? "Supprimer" : "Activez Mode Lab"}><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </div>

                </div>
              );
            })
          ) : (
            <div className="py-20 text-center text-zinc-600 font-mono text-xs uppercase tracking-widest">
              Aucune inscription détectée
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* MODAL DÉTAILS COMPLET */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {detailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-4xl max-h-[90vh] bg-zinc-900 border border-zinc-700 shadow-2xl rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-950">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500/30 to-amber-500/30 flex items-center justify-center text-orange-400 font-black border border-orange-500/30 text-lg rounded">
                  {(detailModal.lastName || detailModal.nom || '??').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-zinc-200">
                    {detailModal.lastName || detailModal.nom} {detailModal.firstName || detailModal.prenom}
                  </h3>
                  <p className="text-sm text-zinc-500">{detailModal.email}</p>
                </div>
              </div>
              <button
                onClick={closeDetailModal}
                className="p-2 hover:bg-zinc-800 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations personnelles */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-orange-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Informations Personnelles
                  </h4>
                  
                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Email</label>
                      <div className="text-zinc-300">{detailModal.email || '—'}</div>
                    </div>
                    
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Téléphone</label>
                      <div className="text-zinc-300">{detailModal.phoneNumber || '—'}</div>
                    </div>
                    
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Date de naissance</label>
                      <div className="text-zinc-300">
                        {detailModal.birthDate ? format(new Date(detailModal.birthDate), 'dd/MM/yyyy') : '—'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">ID Étudiant</label>
                      <div className="text-zinc-300 font-mono">
                        {detailModal.studentId || detailModal.id_etudiant || detailModal.userId || '—'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Besoins spéciaux</label>
                      <div className="text-zinc-300">{detailModal.specialNeeds || 'Aucun'}</div>
                    </div>
                  </div>
                </div>

                {/* Statistiques */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-emerald-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Statistiques
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                      <div className="text-[10px] uppercase text-zinc-500 mb-1">Total Pesas</div>
                      <div className="text-2xl font-black text-orange-400">
                        {(detailModal.stats?.totalPesas || 0).toLocaleString()}₧
                      </div>
                    </div>
                    
                    <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                      <div className="text-[10px] uppercase text-zinc-500 mb-1">Progression</div>
                      <div className="text-2xl font-black text-emerald-400">
                        {detailModal.globalProgress || 0}%
                      </div>
                    </div>
                    
                    <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                      <div className="text-[10px] uppercase text-zinc-500 mb-1">Activités</div>
                      <div className="text-2xl font-black text-sky-400">
                        {detailModal.stats?.completedActivities || 0}
                      </div>
                    </div>
                    
                    <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                      <div className="text-[10px] uppercase text-zinc-500 mb-1">Score Moyen</div>
                      <div className="text-2xl font-black text-purple-400">
                        {detailModal.stats?.averageScore || 0}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                    <div className="text-[10px] uppercase text-zinc-500 mb-1">Temps d'étude</div>
                    <div className="text-xl font-black text-amber-400">
                      {Math.round((detailModal.stats?.studyTimeMinutes || 0) / 60)}h 
                      {(detailModal.stats?.studyTimeMinutes || 0) % 60}min
                    </div>
                  </div>
                </div>

                {/* Cohorte */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Cohorte
                  </h4>
                  
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                    <div className="text-[10px] uppercase text-zinc-500 mb-2">Promotion</div>
                    <div className="text-lg font-bold text-zinc-200">
                      {cohortes.find(c =>
                        c._id === (detailModal.cohorte?._id || detailModal.cohorteId)
                      )?.nom || '—'}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-purple-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Tags
                  </h4>
                  
                  <div className="flex flex-wrap gap-2">
                    {detailModal.tags && detailModal.tags.length > 0 ? (
                      detailModal.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-purple-950/30 border border-purple-800/30 text-purple-300 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-zinc-500 text-sm">Aucun tag</span>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="md:col-span-2 space-y-4">
                  <h4 className="text-sm font-bold text-amber-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                    Notes
                  </h4>
                  
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800 text-sm text-zinc-300">
                    {detailModal.notes || 'Aucune note'}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-6 border-t border-zinc-800 flex justify-end gap-3">
                <button
                  onClick={() => {
                    startEditLearner(detailModal);
                    closeDetailModal();
                  }}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded flex items-center gap-2"
                >
                  <Edit size={14} />
                  Éditer
                </button>
                <button
                  onClick={() => {
                    openCompose(detailModal);
                    closeDetailModal();
                  }}
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded flex items-center gap-2"
                >
                  <Mail size={14} />
                  Contacter
                </button>
                <button
                  onClick={closeDetailModal}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL HISTORIQUE (placeholder) */}
      {historyModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-700 shadow-2xl rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-purple-400">
                Historique des modifications
              </h3>
              <button
                onClick={() => setHistoryModal(null)}
                className="p-2 hover:bg-zinc-800 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div className="text-sm text-zinc-400">
                📅 Fonctionnalité en développement
              </div>
              <div className="text-xs text-zinc-500">
                L'historique des modifications sera disponible prochainement avec :
                - Journal des changements
                - Audit trail complet
                - Rollback possible
                - Export des logs
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setHistoryModal(null)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}